import React from "react";
import { UserProfile } from "../../types";
import { formatDate } from "../../lib/utils";
import DateOfBirthInput from "../ui/DateOfBirthInput";
import Badge from "../ui/Badge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserCircle } from "@fortawesome/free-regular-svg-icons";

interface UserInfoDisplayProps {
  profile: UserProfile;
  isadmin: boolean
}

const InfoItem: React.FC<{
  label: string;
  value: React.ReactNode;
  title?: string;
}> = ({ label, value, title }) => (
  <div title={title} className="truncate">
    <dt className="text-sm font-medium text-text-secondary">{label}</dt>
    <dd className="mt-1 text-sm text-text-primary truncate">
      {value || "N/A"}
    </dd>
  </div>
);

const UserInfoDisplay: React.FC<UserInfoDisplayProps> = ({ profile, isadmin }) => {


  return (
    <div className="p-5 sm:p-6 md:p-6">
      {/* Section Title with Icon */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center mb-5 sm:mb-6 space-y-3 sm:space-y-0">
        <div className="w-10 h-10 sm:w-11 sm:h-11 bg-golden-accent/20 rounded-full flex items-center justify-center sm:mr-4 flex-shrink-0">
          <FontAwesomeIcon
            icon={faUserCircle}
            className="text-golden-accent text-lg sm:text-xl"
          />
        </div>
                 <div className="text-center sm:text-left min-w-0 flex-1">
           <h2 className="text-xl sm:text-2xl font-bold text-text-primary">Account Information</h2>
           <p className="text-text-secondary text-xs sm:text-sm leading-relaxed">Your personal details and account status</p>
         </div>
      </div>

      {/* Enhanced Definition List for key-value pairs */}
      <dl className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6">
        <div className="space-y-4 sm:space-y-5">
          <div className="group">
            <dt className="text-xs sm:text-sm font-medium text-golden-accent uppercase tracking-wide mb-2">
              {isadmin ? "Login ID" : "Phone Number"}
            </dt>
            <dd className="text-base sm:text-lg text-text-primary font-medium bg-dark-tertiary/50 px-3 sm:px-4 py-2 sm:py-3 rounded-lg border border-dark-border/30 group-hover:border-golden-accent/30 transition-colors duration-200">
              {isadmin ? profile.loginId : profile.phone}
            </dd>
          </div>

          <div className="group">
            <dt className="text-xs sm:text-sm font-medium text-golden-accent uppercase tracking-wide mb-2">
              Assigned Role
            </dt>
            <dd className="text-base sm:text-lg text-text-primary font-medium bg-dark-tertiary/50 px-3 sm:px-4 py-2 sm:py-3 rounded-lg border border-dark-border/30 group-hover:border-golden-accent/30 transition-colors duration-200">
              <span className="inline-flex items-center">
                <div className="w-2 h-2 bg-golden-accent rounded-full mr-2 sm:mr-3"></div>
                {profile.role}
              </span>
            </dd>
          </div>

          {profile.name && (
            <div className="group">
              <dt className="text-xs sm:text-sm font-medium text-golden-accent uppercase tracking-wide mb-2">
                Full Name
              </dt>
              <dd className="text-base sm:text-lg text-text-primary font-medium bg-dark-tertiary/50 px-3 sm:px-4 py-2 sm:py-3 rounded-lg border border-dark-border/30 group-hover:border-golden-accent/30 transition-colors duration-200">
                {profile.name}
              </dd>
            </div>
          )}
        </div>

        <div className="space-y-4 sm:space-y-5">
          {!isadmin && (
            <div className="group">
              <dt className="text-xs sm:text-sm font-medium text-golden-accent uppercase tracking-wide mb-2">
                Account Verified
              </dt>
              <dd className="bg-dark-tertiary/50 px-3 sm:px-4 py-2 sm:py-3 rounded-lg border border-dark-border/30 group-hover:border-golden-accent/30 transition-colors duration-200">
                <div className="flex items-center">
                  <Badge
                    status={profile.isVerified ? "active" : "inactive"}
                    size="sm"
                  />
                  <span className="ml-2 sm:ml-3 text-text-primary font-medium text-sm sm:text-base">
                    {profile.isVerified ? "Verified Account" : "Pending Verification"}
                  </span>
                </div>
              </dd>
            </div>
          )}

          <div className="group">
            <dt className="text-xs sm:text-sm font-medium text-golden-accent uppercase tracking-wide mb-2">
              Member Since
            </dt>
            <dd className="text-base sm:text-lg text-text-primary font-medium bg-dark-tertiary/50 px-3 sm:px-4 py-2 sm:py-3 rounded-lg border border-dark-border/30 group-hover:border-golden-accent/30 transition-colors duration-200 flex items-center">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-functional-info mr-2 sm:mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
              </svg>
              {formatDate(profile.createdAt)}
            </dd>
          </div>


        </div>
      </dl>
    </div>
  );
};

export default UserInfoDisplay;
